import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AppState} from 'store/customer/storeSetup';
import {
    RoomType,
    RectangularDimension,
    LShapedDimension,
    RoomPlannerProduct,
    ActiveGroupPosition,
    RoomPlannerLayout,
    HistoryStack,
} from 'components/customer/RoomPlanner/types';
import {
    PreviewPosition,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import * as THREE from 'three';
import {placeholderSceneId} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';
import {addToHistoryStackHelper} from 'components/customer/RoomPlanner/helpers/history';
import {addPositionChangeToHistory} from 'components/customer/RoomPlanner/helpers/history/positionChangeAction';
import {
    DuplicateIds,
    removeDuplicateIdIndex,
    upsertDuplicateIdList,
} from 'components/customer/RoomPlanner/helpers/duplicateIdsHelpers';
import {
    DoorFrontsMode,
    DoorFrontsPose,
} from 'components/customer/RoomPlanner/helpers/doorFronts';
import {
    PlannerViewMode,
    viewStateForPreset,
} from 'components/customer/RoomPlanner/helpers/plannerView';

export type {DuplicateIds};
export type AccumulatedPositionDeltas = {
    [key: string]: {x: number; y: number; z: number};
};
export interface RoomPlanner {
    /** Global flag indicating an async history/room-planner operation is in progress. */
    isBusy: boolean;
    isRoomPlannerEditing: boolean;
    isDragging: boolean;
    activeGroupPosition: ActiveGroupPosition | null;
    scale: number;
    roomLayoutModalOpen: boolean;
    roomType: RoomType | null;
    rectangularDimension: RectangularDimension;
    lShapedDimension: LShapedDimension;
    products?: RoomPlannerProduct[];
    previewPosition: PreviewPosition | null;
    plannerViewMode: PlannerViewMode;
    activeViewPreset: PreviewPosition | null;
    doorFrontsMode: DoorFrontsMode;
    doorFrontsPose: DoorFrontsPose;
    doorFrontsAnimating: boolean;
    zoom: number;
    showArrows: boolean;
    showCabinets: boolean;
    selectedProductValue: RoomPlannerProduct | null;
    productValuesForUpdate: PreviewFormValues | null;
    isAutoSaving: boolean;
    hiddenWall: string | null;
    isTopView: boolean;
    rectangularElementDimension: RectangularDimension | null;
    lShapedElementDimension: LShapedDimension | null;
    topViewZoom: number;
    isEditHeightView: boolean;
    isWireframeMode: boolean;
    isRotating?: boolean;
    isPDFView: boolean;
    isViewingAtBottom: boolean;
    backupProductValues: RoomPlannerProduct | null;
    layoutData: RoomPlannerLayout | null;
    isEditingRoomDimension: boolean;
    layoutId: number | null;
    duplicateIds: DuplicateIds;
    isCreatingSnapshots: boolean;
    deletedIds: number[];
    isDeleting: boolean;
    oldCabinets?: CabinetTransform[];
    /** Placeholder ids in the scene so layout rebuilds after add/delete. Pose lives on the mesh + API. */
    placeholderIds: string[];
    historyStack: HistoryStack[];
    accumulatedPositionDeltas: AccumulatedPositionDeltas;
    /** Current room index (0-based) for history titles e.g. [1-6] = roomIndex+1 and room_cab_number */
    roomIndex: number | null;
    selectedMenu: string;
}

interface UpdateDimensionPayload {
    field: string;
    value: number;
    type: RoomType;
}

export interface CabinetTransform {
    id: string;
    position: {
        x: number;
        y: number;
        z: number;
    };
    rotation: {
        x: number;
        y: number;
        z: number;
    };
}

const initialState = {
    isBusy: false,
    isRoomPlannerEditing: false,
    isDragging: false,
    scale: 0.2,
    roomLayoutModalOpen: true,
    roomType: null,
    rectangularDimension: {
        length: 8000,
        width: 8000,
        height: 2800,
    },
    lShapedDimension: {
        leftWidth: 8000,
        rightWidth: 8000,
        leftDepth: 4200,
        rightDepth: 4200,
        height: 2800,
    },
    previewPosition: 'TOP',
    plannerViewMode: '3D',
    activeViewPreset: 'RIGHT',
    doorFrontsMode: 'shut',
    doorFrontsPose: 'shut',
    doorFrontsAnimating: false,
    zoom: 1,
    showArrows: false,
    showCabinets: true,
    selectedProductValue: null,
    productValuesForUpdate: null,
    isAutoSaving: false,
    hiddenWall: null,
    isTopView: true,
    rectangularElementDimension: null,
    lShapedElementDimension: null,
    topViewZoom: 1,
    isEditHeightView: false,
    isWireframeMode: false,
    isRotating: false,
    isViewingAtBottom: false,
    backupProductValues: null,
    layoutData: null,
    isEditingRoomDimension: false,
    layoutId: null,
    duplicateIds: {},
    isCreatingSnapshots: false,
    deletedIds: [],
    isDeleting: false,
    placeholderIds: [],
    historyStack: [],
    accumulatedPositionDeltas: {},
    roomIndex: null,
    selectedMenu: '',
} as RoomPlanner;

const roomPlannerSlice = createSlice({
    name: 'room_planner',
    initialState,
    reducers: {
        selectedMenuSet: (state, {payload}: PayloadAction<string>) => {
            state.selectedMenu = payload;
        },
        duplicateIdsSetAll: (state, {payload}: PayloadAction<DuplicateIds>) => {
            state.duplicateIds = payload;
        },
        duplicateIdsSet: {
            reducer: (
                state,
                {
                    payload,
                }: PayloadAction<{
                    cabinetId: number | string;
                    duplicateIds: number[];
                }>
            ) => {
                const {cabinetId, duplicateIds} = payload;
                state.duplicateIds = upsertDuplicateIdList(
                    state.duplicateIds,
                    cabinetId,
                    duplicateIds
                );
            },
            prepare: (cabinetId: number | string, duplicateIds: number[]) => {
                return {
                    payload: {cabinetId, duplicateIds},
                };
            },
        },
        duplicateIdDelete: {
            reducer: (
                state,
                {
                    payload,
                }: PayloadAction<{
                    cabinetId: number | string;
                    duplicateId: number;
                }>
            ) => {
                const {cabinetId, duplicateId} = payload;
                state.duplicateIds = removeDuplicateIdIndex(
                    state.duplicateIds,
                    cabinetId,
                    duplicateId
                );
            },
            prepare: (cabinetId: number | string, duplicateId: number) => {
                return {
                    payload: {cabinetId, duplicateId},
                };
            },
        },
        rotatingSet: (state, {payload}: PayloadAction<boolean>) => {
            state.isRotating = payload;
        },
        setIsRoomPlannerEditing: (state, action: PayloadAction<boolean>) => {
            state.isRoomPlannerEditing = action.payload;
        },
        setIsDragging: (state, action: PayloadAction<boolean>) => {
            state.isDragging = action.payload;
        },
        setActiveGroupPosition: (
            state,
            action: PayloadAction<ActiveGroupPosition | null>
        ) => {
            state.activeGroupPosition = action.payload;
        },
        setRoomType: (state, action: PayloadAction<RoomType | null>) => {
            state.roomType = action.payload;
        },
        setRoomLayoutModalOpen: (state, action: PayloadAction<boolean>) => {
            state.roomLayoutModalOpen = action.payload;
        },
        updateDimension: (
            state,
            action: PayloadAction<UpdateDimensionPayload>
        ) => {
            if (action.payload.type === 'RECTANGULAR') {
                state.rectangularDimension[
                    action.payload
                        .field as keyof typeof state.rectangularDimension
                ] = action.payload.value;
            }

            if (action.payload.type === 'LSHAPED') {
                state.lShapedDimension[
                    action.payload.field as keyof typeof state.lShapedDimension
                ] = action.payload.value;
            }
        },
        setProducts: (state, action: PayloadAction<RoomPlannerProduct[]>) => {
            state.products = action.payload;
        },
        setDeletedId: (state, {payload}: PayloadAction<number>) => {
            state.deletedIds.push(payload);
        },
        setPreviewPosition: (state, action: PayloadAction<PreviewPosition>) => {
            if (action.payload) state.isTopView = action.payload === 'TOP';
            if (action.payload)
                state.isEditHeightView =
                    action.payload === 'LEFT_LEVELED_STATIC';
            state.previewPosition = action.payload;
            const view = viewStateForPreset(action.payload);
            if (view) {
                state.plannerViewMode = view.mode;
                state.activeViewPreset = view.preset;
            }
        },
        setPlannerView: (
            state,
            action: PayloadAction<{
                mode: PlannerViewMode;
                preset: PreviewPosition | null;
            }>
        ) => {
            state.plannerViewMode = action.payload.mode;
            state.activeViewPreset = action.payload.preset;
        },
        setDoorFrontsMode: (state, action: PayloadAction<DoorFrontsMode>) => {
            state.doorFrontsMode = action.payload;
            if (action.payload !== 'hide') {
                state.doorFrontsPose = action.payload;
            }
        },
        setDoorFrontsAnimating: (state, action: PayloadAction<boolean>) => {
            state.doorFrontsAnimating = action.payload;
        },
        setIsTopView: (state, action: PayloadAction<boolean>) => {
            state.isTopView = action.payload;
        },
        setZoom: (state, action: PayloadAction<number>) => {
            state.zoom = action.payload;
        },
        setShowArrows: (state, action: PayloadAction<boolean>) => {
            state.showArrows = action.payload;
        },
        setShowCabinets: (state, action: PayloadAction<boolean>) => {
            state.showCabinets = action.payload;
        },
        setSelectedProductValue: (
            state,
            action: PayloadAction<RoomPlannerProduct | null>
        ) => {
            state.selectedProductValue = action.payload;
        },
        setProductValuesForUpdate: (
            state,
            action: PayloadAction<PreviewFormValues | null>
        ) => {
            state.productValuesForUpdate = action.payload;
        },
        setIsAutoSaving: (state, action: PayloadAction<boolean>) => {
            state.isAutoSaving = action.payload;
        },
        setHiddenWall: (state, action: PayloadAction<string | null>) => {
            state.hiddenWall = action.payload;
        },
        setRectangularElementDimension: (
            state,
            action: PayloadAction<RectangularDimension | null>
        ) => {
            state.rectangularElementDimension = action.payload;
        },
        setLShapedElementDimension: (
            state,
            action: PayloadAction<LShapedDimension | null>
        ) => {
            state.lShapedElementDimension = action.payload;
        },
        setTopViewZoom: (state, action: PayloadAction<number>) => {
            state.topViewZoom = action.payload;
        },
        setIsWireframeMode: (state, action: PayloadAction<boolean>) => {
            state.isWireframeMode = action.payload;
        },
        setIsViewingAtBottom: (state, action: PayloadAction<boolean>) => {
            state.isViewingAtBottom = action.payload;
        },
        setBackupProductValues: (
            state,
            action: PayloadAction<RoomPlannerProduct | null>
        ) => {
            state.backupProductValues = action.payload;
        },
        setLShapedDimension: (
            state,
            action: PayloadAction<LShapedDimension>
        ) => {
            state.lShapedDimension = action.payload;
        },
        setRectangularDimension: (
            state,
            action: PayloadAction<RectangularDimension>
        ) => {
            state.rectangularDimension = action.payload;
        },
        setLayoutData: (
            state,
            action: PayloadAction<RoomPlannerLayout | null>
        ) => {
            state.layoutData = action.payload;
        },
        setIsEditingRoomDimension: (state, action: PayloadAction<boolean>) => {
            state.isEditingRoomDimension = action.payload;
        },
        setLayoutId: (state, action: PayloadAction<number | null>) => {
            state.layoutId = action.payload;
        },
        setIsEditHeightView: (state, action: PayloadAction<boolean>) => {
            state.isEditHeightView = action.payload;
        },
        setIsCreatingSnapshots: (state, action: PayloadAction<boolean>) => {
            state.isCreatingSnapshots = action.payload;
        },
        setIsBusy: (state, action: PayloadAction<boolean>) => {
            state.isBusy = action.payload;
        },
        setIsDeleting: (state, action: PayloadAction<boolean>) => {
            state.isDeleting = action.payload;
        },
        setOldCabinetPosition: (
            state,
            action: PayloadAction<CabinetTransform[]>
        ) => {
            state.oldCabinets = action.payload;
        },
        updateCabinetPosition: (
            state,
            {
                payload,
            }: PayloadAction<{
                rotation: THREE.Euler;
                position: THREE.Vector3;
                id: number;
                index?: number;
                skipHistory?: boolean;
                roomIndex?: number;
                roomCabNumber?: number;
                typeName?: string;
            }>
        ) => {
            const {
                rotation,
                position,
                id,
                index,
                skipHistory,
                roomIndex,
                roomCabNumber,
                typeName,
            } = payload;
            const pos = new THREE.Vector3(position.x, position.y, position.z);
            const rot = new THREE.Euler(
                rotation.x,
                rotation.y,
                rotation.z,
                rotation.order || 'XYZ'
            );

            const selectedCabinet = state.layoutData?.layout_room_products.find(
                (cabinet) =>
                    cabinet.job_cabinet_id == id &&
                    (index == undefined || cabinet.index == index)
            );

            // Store the initial position and rotation before updating (for undo).
            // Clone so we capture the previous state, not a reference that may change.
            const initialPosition = selectedCabinet
                ? selectedCabinet.position instanceof THREE.Vector3
                    ? selectedCabinet.position.clone()
                    : new THREE.Vector3(
                          (
                              selectedCabinet.position as {
                                  x: number;
                                  y: number;
                                  z: number;
                              }
                          ).x,
                          (
                              selectedCabinet.position as {
                                  x: number;
                                  y: number;
                                  z: number;
                              }
                          ).y,
                          (
                              selectedCabinet.position as {
                                  x: number;
                                  y: number;
                                  z: number;
                              }
                          ).z
                      )
                : pos;
            // Room planner only rotates cabinets around Z (compass direction).
            // Store only Z so undo/redo never overwrites X/Y (e.g. manual panel -90°).
            const prevRot = selectedCabinet?.rotation;
            const prevZ =
                prevRot != null
                    ? ((prevRot as {z?: number}).z ??
                      (prevRot as {_z?: number})._z ??
                      rot.z)
                    : rot.z;
            const initialRotation = new THREE.Euler(0, 0, prevZ, 'XYZ');

            if (!selectedCabinet) {
                state.layoutData?.layout_room_products.push({
                    job_cabinet_id: id,
                    rotation: rot,
                    position: pos,
                    index,
                });
            } else {
                state.layoutData?.layout_room_products.forEach((cabinet) => {
                    if (
                        cabinet.job_cabinet_id === payload.id &&
                        (index == undefined || cabinet.index == index)
                    ) {
                        cabinet.rotation = rot;
                        cabinet.position = pos;
                        cabinet.index = index;
                    }
                });
            }

            // Skip adding to history if flag is set (e.g., during undo/redo)
            if (skipHistory) {
                return;
            }

            addPositionChangeToHistory(state, {
                position: pos,
                rotation: rot,
                id,
                index,
                initialPosition,
                initialRotation,
                roomIndex,
                roomCabNumber,
                typeName,
            });
        },
        updatePlaceholderPosition: (
            state,
            {
                payload,
            }: PayloadAction<{
                rotation: THREE.Euler;
                position: THREE.Vector3;
                id: string;
                placeholderId?: number;
                skipHistory?: boolean;
                typeName?: string;
                currentDirection?: string;
                groupId?: string;
                copyIndex?: number;
            }>
        ) => {
            const {
                rotation,
                position,
                id,
                placeholderId,
                skipHistory,
                typeName,
                currentDirection,
                groupId,
                copyIndex,
            } = payload;
            const pos = new THREE.Vector3(position.x, position.y, position.z);
            const rot = new THREE.Euler(
                rotation.x,
                rotation.y,
                rotation.z,
                rotation.order || 'XYZ'
            );

            if (
                state.layoutData &&
                !state.layoutData.layout_room_placeholders
            ) {
                state.layoutData.layout_room_placeholders = [];
            }

            const placeholders = state.layoutData?.layout_room_placeholders;
            const selected = placeholders?.find(
                (item) => placeholderSceneId(item) === id
            );

            const initialPosition = selected
                ? new THREE.Vector3(
                      selected.position.x,
                      selected.position.y,
                      selected.position.z
                  )
                : pos.clone();
            const prevY =
                selected?.rotation != null
                    ? ((selected.rotation as {y?: number}).y ??
                      (selected.rotation as {_y?: number})._y ??
                      rot.y)
                    : rot.y;
            const initialRotation = new THREE.Euler(0, prevY, 0, 'XYZ');

            const pose = {
                x: pos.x,
                y: pos.y,
                z: pos.z,
            };
            const rotPose = {
                x: rot.x,
                y: rot.y,
                z: rot.z,
            };

            if (!selected && placeholders) {
                placeholders.push({
                    layout_room_id: state.layoutId ?? 0,
                    placeholder_id: placeholderId ?? 0,
                    scene_id: id,
                    group_id: groupId,
                    index: copyIndex,
                    current_direction: currentDirection ?? 'front',
                    position: pose,
                    rotation: rotPose,
                });
            } else if (selected) {
                selected.position = pose;
                selected.rotation = rotPose;
                if (currentDirection) {
                    selected.current_direction = currentDirection;
                }
                if (groupId) {
                    selected.group_id = groupId;
                    selected.index = copyIndex;
                }
            }

            if (skipHistory) {
                return;
            }

            addPositionChangeToHistory(state, {
                position: pos,
                rotation: rot,
                id,
                initialPosition,
                initialRotation,
                typeName,
                isPlaceholder: true,
            });
        },
        registerPlaceholderId: (state, {payload}: PayloadAction<string>) => {
            if (payload && !state.placeholderIds.includes(payload)) {
                state.placeholderIds.push(payload);
            }
        },
        unregisterPlaceholderId: (state, {payload}: PayloadAction<string>) => {
            state.placeholderIds = state.placeholderIds.filter(
                (id) => id !== payload
            );
        },
        setToInitialState: () => initialState,
        addToHistoryStack: (state, {payload}: PayloadAction<HistoryStack>) => {
            // Clear redo history when adding any action
            addToHistoryStackHelper(state, payload, {
                clearAfterActive: true,
            });
        },
        setHistoryStackActiveIndex: (
            state,
            {payload}: PayloadAction<number>
        ) => {
            // Set all items to inactive
            state.historyStack.forEach((item) => {
                item.isActive = false;
            });

            // Set the item at the specified index to active
            if (
                payload >= 0 &&
                payload < state.historyStack.length &&
                state.historyStack[Number(payload)]
            ) {
                state.historyStack[Number(payload)].isActive = true;
            }
        },
        updateHistoryItemDataId: (
            state,
            {payload}: PayloadAction<{index: number; newId: number}>
        ) => {
            const {index, newId} = payload;
            if (
                index >= 0 &&
                index < state.historyStack.length &&
                state.historyStack[Number(index)]?.data
            ) {
                state.historyStack[Number(index)].data.id = newId;
            }
        },
        updateHistoryItemData: (
            state,
            {
                payload,
            }: PayloadAction<{
                index: number;
                data: Partial<HistoryStack['data']>;
            }>
        ) => {
            const {index, data} = payload;
            if (
                index >= 0 &&
                index < state.historyStack.length &&
                state.historyStack[Number(index)]?.data
            ) {
                Object.assign(state.historyStack[Number(index)].data, data);
            }
        },
        updateHistoryItemTitle: (
            state,
            {
                payload,
            }: PayloadAction<{
                index: number;
                title: string;
            }>
        ) => {
            const {index, title} = payload;
            if (index >= 0 && index < state.historyStack.length) {
                state.historyStack[Number(index)].title = title;
            }
        },
        // Patches every CABINET_ADD history entry whose data.id matches
        // cabinetId, replacing the "[roomIdx-...]" or "[roomIdx-N]" placeholder
        // with the authoritative room_cab_number from the server.
        // Running the search inside the reducer guarantees it operates on the
        // live state at dispatch time — no stale index captured in a closure.
        patchCabinetAddHistoryTitleByCabinetId: (
            state,
            {
                payload,
            }: PayloadAction<{
                cabinetId: number;
                roomCabNumber: number;
            }>
        ) => {
            const {cabinetId, roomCabNumber} = payload;
            state.historyStack.forEach((item) => {
                if (
                    item.action !== 'CABINET_ADD' ||
                    Number(item.data?.id) !== cabinetId
                ) {
                    return;
                }
                const updated = (item.title ?? '').replace(
                    /\[(\d+)-(?:\d+|\.\.\.)\]/,
                    (_match, roomIndexStr: string) =>
                        `[${roomIndexStr}-${roomCabNumber}]`
                );
                if (updated !== item.title) {
                    item.title = updated;
                }
            });
        },
        setHistoryItemRecentlyRedone: (
            state,
            {payload}: PayloadAction<{index: number; recentlyRedone: boolean}>
        ) => {
            const {index, recentlyRedone} = payload;

            if (index >= 0 && index < state.historyStack.length) {
                state.historyStack[Number(index)].recentlyRedone =
                    recentlyRedone;
            }
        },
        setHistoryItemRecentlyUndone: (
            state,
            {payload}: PayloadAction<{index: number; recentlyUndone: boolean}>
        ) => {
            const {index, recentlyUndone} = payload;

            if (index >= 0 && index < state.historyStack.length) {
                state.historyStack[Number(index)].recentlyUndone =
                    recentlyUndone;
            }
        },
        setRoomPlannerRoomIndex: (
            state,
            {payload}: PayloadAction<number | null>
        ) => {
            state.roomIndex = payload;
        },
    },
});

export const getScale = (state: AppState) => state.roomPlanner.scale;
export const getRoomType = (state: AppState) => state.roomPlanner.roomType;
export const getRoomLayoutModalOpen = (state: AppState) =>
    state.roomPlanner.roomLayoutModalOpen;
export const getActiveGroupPosition = (state: AppState) =>
    state.roomPlanner.activeGroupPosition;

export const getIsDragging = (state: AppState) => state.roomPlanner.isDragging;
export const getIsCreatingSnapshots = (state: AppState) =>
    state.roomPlanner.isCreatingSnapshots;
export const getisRoomPlannerEditing = (state: AppState) =>
    state.roomPlanner.isRoomPlannerEditing;

export const getPreviewPosition = (state: AppState) =>
    state.roomPlanner.previewPosition;
export const getPlannerViewMode = (state: AppState) =>
    state.roomPlanner.plannerViewMode;
export const getActiveViewPreset = (state: AppState) =>
    state.roomPlanner.activeViewPreset;
export const getDoorFrontsMode = (state: AppState) =>
    state.roomPlanner.doorFrontsMode;
export const getDoorFrontsPose = (state: AppState) =>
    state.roomPlanner.doorFrontsPose;
export const getDoorFrontsAnimating = (state: AppState) =>
    state.roomPlanner.doorFrontsAnimating;

export const getRoomDimensions = (state: AppState) => ({
    rectangularDimension: state.roomPlanner.rectangularDimension,
    lShapedDimension: state.roomPlanner.lShapedDimension,
});
export const getProducts = (state: AppState) => state.roomPlanner.products;
/**
 * Stable key of placeholder ids so wall measurements rebuild after restore/add/delete.
 * @param {AppState} state - Root Redux state
 * @return {string} Sorted comma-separated placeholder ids
 */
export const getPlaceholderIdsKey = (state: AppState) =>
    [...state.roomPlanner.placeholderIds].sort().join(',');

export const getZoom = (state: AppState) => state.roomPlanner.zoom;
export const getShowArrows = (state: AppState) => state.roomPlanner.showArrows;
export const getShowCabinets = (state: AppState) =>
    state.roomPlanner.showCabinets;
export const getSelectedProductValue = (state: AppState) =>
    state.roomPlanner.selectedProductValue;
export const getProductValuesForUpdate = (state: AppState) =>
    state.roomPlanner.productValuesForUpdate;
export const getIsAutoSaving = (state: AppState) =>
    state.roomPlanner.isAutoSaving;
export const getHiddenWall = (state: AppState) => state.roomPlanner.hiddenWall;
export const getIsTopView = (state: AppState) => state.roomPlanner.isTopView;
export const getRectangularElementDimension = (state: AppState) =>
    state.roomPlanner.rectangularElementDimension;
export const getTopViewZoom = (state: AppState) =>
    state.roomPlanner.topViewZoom;
export const getIsEditHeightView = (state: AppState) =>
    state.roomPlanner.isEditHeightView;
export const getLShapedElementDimension = (state: AppState) =>
    state.roomPlanner.lShapedElementDimension;
export const getIsWireframeMode = (state: AppState) =>
    state.roomPlanner.isWireframeMode;
export const getIsRotating = (state: AppState) => state.roomPlanner.isRotating;
export const getIsViewingAtBottom = (state: AppState) =>
    state.roomPlanner.isViewingAtBottom;
export const getBackupProductValues = (state: AppState) =>
    state.roomPlanner.backupProductValues;
export const getLayoutData = (state: AppState) => state.roomPlanner.layoutData;
export const getIsEditingRoomDimension = (state: AppState) =>
    state.roomPlanner.isEditingRoomDimension;
export const getLayoutId = (state: AppState) => state.roomPlanner.layoutId;
export const getDuplicateIds = (state: AppState) =>
    state.roomPlanner.duplicateIds;
export const getIsDeleting = (state: AppState) => state.roomPlanner.isDeleting;
export const getIsBusy = (state: AppState) => state.roomPlanner.isBusy;

export const getOldCabinetsPosition = (state: {
    roomPlanner: typeof initialState;
}) => state.roomPlanner.oldCabinets;
export const getHistoryStack = (state: AppState) =>
    state.roomPlanner.historyStack;
export const getRoomPlannerRoomIndex = (state: AppState) =>
    state.roomPlanner.roomIndex;
export const getSelectedMenu = (state: AppState) =>
    state.roomPlanner.selectedMenu;

export const {
    setActiveGroupPosition,
    setIsDragging,
    setIsRoomPlannerEditing,
    setRoomType,
    setRoomLayoutModalOpen,
    updateDimension,
    setProducts,
    setPreviewPosition,
    setPlannerView,
    setDoorFrontsMode,
    setDoorFrontsAnimating,
    setZoom,
    setShowArrows,
    setShowCabinets,
    setSelectedProductValue,
    setProductValuesForUpdate,
    setIsAutoSaving,
    setHiddenWall,
    setRectangularElementDimension,
    setLShapedElementDimension,
    setTopViewZoom,
    setIsWireframeMode,
    rotatingSet,
    setIsViewingAtBottom,
    setBackupProductValues,
    setLShapedDimension,
    setRectangularDimension,
    setIsTopView,
    setLayoutData,
    setIsEditingRoomDimension,
    setLayoutId,
    setIsEditHeightView,
    setOldCabinetPosition,
    updateCabinetPosition,
    updatePlaceholderPosition,
    registerPlaceholderId,
    unregisterPlaceholderId,
    duplicateIdsSetAll,
    duplicateIdsSet,
    duplicateIdDelete,
    setIsCreatingSnapshots,
    setIsBusy,
    setIsDeleting,
    setToInitialState,
    setDeletedId,
    addToHistoryStack,
    setHistoryStackActiveIndex,
    updateHistoryItemDataId,
    updateHistoryItemData,
    setHistoryItemRecentlyRedone,
    setHistoryItemRecentlyUndone,
    setRoomPlannerRoomIndex,
    updateHistoryItemTitle,
    patchCabinetAddHistoryTitleByCabinetId,
    selectedMenuSet,
} = roomPlannerSlice.actions;

export default roomPlannerSlice.reducer;
